import { HiFolder } from "react-icons/hi2";
import { useTeamStorage } from "../../lib/react-query/teams";
import { Spinner } from "../shared";
import { cn } from "../../lib/cn";
import TeamStorageFileManager from "./TeamStorageFileManager";

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const StatBox = ({ label, value, borderColor }) => (
  <div className={`border rounded-lg p-3 border-l-2 bg-white ${borderColor}`}>
    <p className="text-xs text-grey">{label}</p>
    <p className="text-xl font-semibold text-darkgrey">{value}</p>
  </div>
);

const TeamStorageSection = ({ teamId, onOpenProjectFiles = null, activeProjectId = null }) => {
  const { data: storage, isLoading, isError } = useTeamStorage(teamId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner width={24} height={24} />
      </div>
    );
  }

  if (isError || !storage) {
    return (
      <div className="bg-white rounded-xl border border-surface-border p-5">
        <h2 className="text-base font-semibold text-darkgrey mb-2">Files</h2>
        <div className="rounded-lg bg-danger/5 px-4 py-3 text-sm text-danger">
          Could not load files.
        </div>
      </div>
    );
  }

  const usedBytes = storage.used_bytes ?? storage.total_used_bytes ?? 0;
  const quotaBytes = storage.quota_bytes ?? storage.storage_quota_bytes ?? 5 * 1024 * 1024 * 1024;
  const rawPercent = Math.round((usedBytes / Math.max(quotaBytes, 1)) * 100);
  const percent = Math.min(rawPercent, 100);
  const isOverLimit = rawPercent > 100;
  const displayPercent = isOverLimit ? rawPercent : percent;
  const projectUsage = Array.isArray(storage.by_project)
    ? storage.by_project
    : Array.isArray(storage.project_usage)
      ? storage.project_usage
      : Array.isArray(storage.projects)
        ? storage.projects
        : [];

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl p-4 border border-surface-border">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatBox label="Total used" value={formatBytes(usedBytes)} borderColor="border-l-secondary/60" />
          <StatBox label="Quota" value={formatBytes(quotaBytes)} borderColor="border-l-primary/40" />
          <StatBox label="Usage" value={`${displayPercent}%`} borderColor={isOverLimit ? "border-l-danger" : "border-l-warning/60"} />
        </div>
        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-surface-hover">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              isOverLimit ? "bg-danger" : percent < 60 ? "bg-secondary" : percent < 80 ? "bg-warning" : "bg-danger"
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
        {isOverLimit && (
          <p className="mt-2 text-xs font-medium text-danger">
            Storage limit exceeded. Consider removing unused files to free up space.
          </p>
        )}
        <p className="mt-2 text-xs text-mediumgrey">
          Open a project folder to browse and manage files.
        </p>
      </div>

      <section className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border bg-surface-hover/50">
          <h2 className="text-base font-semibold text-darkgrey">Team files</h2>
        </div>
        <div className="p-4">
          <TeamStorageFileManager teamId={teamId} />
        </div>
      </section>

      <section className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border bg-surface-hover/50">
          <h2 className="text-base font-semibold text-darkgrey">Project folders</h2>
          <span className="text-xs text-mediumgrey">
            {projectUsage.length} {projectUsage.length === 1 ? "project" : "projects"}
          </span>
        </div>

        {projectUsage.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
            {projectUsage.map((item) => {
              const projectId = item.project_id || item.id;
              const projectName = item.project_name || item.name || "Project";
              const isActive = activeProjectId === projectId;

              return (
                <button
                  key={projectId || projectName}
                  type="button"
                  onClick={() => projectId && onOpenProjectFiles?.(projectId)}
                  className={cn(
                    "flex items-center gap-3 rounded-xl border border-surface-border bg-white p-4 text-left transition-all",
                    projectId ? "hover:border-primary/30 hover:shadow-sm" : "cursor-default",
                    isActive && "border-primary/30 bg-primary/5"
                  )}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary">
                    <HiFolder className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-darkgrey">{projectName}</p>
                    <p className="text-xs text-mediumgrey">
                      {formatBytes(item.used_bytes ?? 0)}
                    </p>
                  </div>
                  {projectId ? (
                    <span className="text-xs font-medium text-primary whitespace-nowrap">
                      Open →
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-sm text-darkgrey">No project folders yet.</p>
            <p className="mt-1 text-xs text-mediumgrey">
              Projects created under this team will appear here.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default TeamStorageSection;
